
import { defineComponent } from 'vue';
import Pagination from '@/components/Pagination/index.vue';
import { GET_CONTACTS } from '@/gql';
import AvgPayment from '@/components/_AvgPayment/index.vue';
import RiskScore from '@/components/_RiskScore/index.vue';
import { mapGetters } from 'vuex';
import Contacts from '@/components/_Contacts/index.vue';
import TranslationHelper from '@/helpers/translation';
import { Risolto_Recipient_Bool_Exp, ViewsRecipient_Recipients, ViewsRecipient_RecipientsQueryVariables, ViewsRecipient_RecipientsTotal, ViewsRecipient_RecipientsTotalQueryVariables } from '@/generated/graphql';
import ViewsRecipient_tags from './tags.graphql';
import Logging from '@/logging';
import gql from 'graphql-tag';
import CustomFieldsFilters from '@/components/_CustomFields/CustomFieldsFilters.vue';
import CustomFieldLabel from '@/components/_CustomFields/CustomFieldLabel.vue';
import CustomFieldValue from '@/components/_CustomFields/CustomFieldValue.vue';

const RECIPIENT_QUERY = gql`
query ViewsRecipient_recipient ($participant: uuid!, $recipient: uuid!) {
  risolto_recipient (where: {
    reference: { _eq: $recipient },
    participant: { _eq: $participant }
  }) {
		legal_entity_by_reference {
			reference
			registration_name
		}
		contacts {
			reference
			first_name
			last_name
		}
		currency
		total_amount: accounting_documents_aggregate {
			aggregate {
				sum {
					total_amount
				}
			}
		},
		open_amount: accounting_documents_aggregate {
			aggregate {
				sum {
					open_amount
				}
			}
		},
		amount_due: accounting_documents_aggregate(
			where: { 
				_or: [
					{ due_date: { _lte: "now()" } },
					{ due_date: { _is_null: true } }
				]
			}
		) {
			aggregate {
				sum {
					open_amount
				}
			}
		}
		prediction_stars
		custom_fields
	}
}`;

const GET_USER_INTERFACE_DETAILS = gql`
query ViewsRecipient_userInterfaceDetails($participant: uuid!) {
	userInterfaceDetails: risolto_participant(
		where: {
			reference: { _eq: $participant }
		}
	) {
		custom_field_definitions: user_interface_details(path: "recipient.custom_field_definitions")
	}
}
`;

export default defineComponent({
	name: 'Recipient',
	apollo: {
		tags: {
			query: ViewsRecipient_tags,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			}
		},
		userInterfaceDetails: {
			query: GET_USER_INTERFACE_DETAILS,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			update: data => data.userInterfaceDetails?.[0]
		}
	},
	components: {
		Pagination,
		AvgPayment,
		RiskScore,
		Contacts,
		CustomFieldsFilters,
		CustomFieldLabel,
		CustomFieldValue
	},
	props: {
		search: { type: String, required: false, default: '' }
	},
	data() {
		return {
			searchTerm: this.search,
			includeContactsInSearch: false,
			userInterfaceDetails: null,
			customFieldsFiltersVisible: [],
			customFieldsFiltersObject: {},
			filterTags: [],
			recipients: undefined,
			contacts: undefined,
			percentage: 10,
			quickViewSelection: null,
			multipleSelection: [],
			total: -1,
			page: 1,
			limit: 25,
			offset: 0,
			orderBy: null,
			loading: false,
			rightPanelLayoutVariables: {
				mainPanelWidth: 17,
				rightPanelWidth: 7
			}
		};
	},
	computed: {
		...mapGetters(['env', 'selectedParticipantStore', 'selectedLegalEntityStore', 'recipientsOverviewRightPanelCollapsed']),
		sortedTags() {
			return [...this.tags || []].sort((a, b) => {
				return (this.$translate.tag(a.tag) < this.$translate.tag(b.tag)) ? -1 : 1;
			});
		}
	},
	watch: {
		recipientsOverviewRightPanelCollapsed() {
			this.evaluateLayout();
		}
	},
	created() {
		if (this.selectedParticipantStore) {
			this.getRecipients();
		}
		this.evaluateLayout();
	},
	methods: {
		onSearch() {
			this.$router.replace({
				query: {
					...this.$route.query,
					search: this.searchTerm
				}
			});
			this.page = 1;
			this.offset = 0;
			this.limit = 25;
			this.getRecipients();
		},
		onSortChange({ prop, order }) {
			Logging.log(prop, order);

			let gqlOrder: string | null = '';

			if (order === 'ascending') {
				gqlOrder = 'asc_nulls_last';
			} else if (order === 'descending') {
				gqlOrder = 'desc_nulls_last';
			} else if (order === null) {
				gqlOrder = null;
			}

			let gqlOrderObject = {};

			if (prop === 'total_amount') {
				gqlOrderObject = { total_amount: { sum: { total_amount: gqlOrder } } };
			} else if (prop === 'open_amount') {
				gqlOrderObject = { open_amount: { sum: { open_amount: gqlOrder } } };
			} else if (prop === 'name') {
				gqlOrderObject = { name: gqlOrder };
			} else {
				//
			}

			let orderBy: object | null = gqlOrderObject;

			if (gqlOrder === null) {
				orderBy = null;
			}

			this.orderBy = orderBy;
			this.getRecipients();
		},
		toggleSelection(rows) {
			if (rows) {
				rows.forEach(row => {
					this.$refs.multipleTable.toggleRowSelection(row);
				});
			} else {
				this.$refs.multipleTable.clearSelection();
			}
		},
		handleFilter() {
			this.page = 1;
			this.offset = 0;
			this.getRecipients();
		},
		handleSelectionChange(val) {
			this.multipleSelection = val;
		},
		handleRowClick(row, column, event) {
			// console.log('row', row);

			this.quickViewSelection = row;

			this.$apollo
				.query({
					query: GET_CONTACTS,
					variables: {
						participant: this.selectedParticipantStore,
						recipient: row.reference
					}
				})
				.then(result => {
					if (result.data) {
						this.contacts = result.data.contacts;

						for (let contact of result.data.contacts) {
							let availableEndpoints = contact.channel_endpoints.filter(el => {
								if (el.channel === 'PHONE' || el.channel === 'EMAIL' || el.channel === 'MAIL') { // TODO: PEPPOL: why two ifs?
									if (el.channel === 'PHONE') el.icon = 'phone-solid';
									if (el.channel === 'EMAIL') el.icon = 'at-solid';
									if (el.channel === 'MAIL') el.icon = 'envelope-solid';
									if (el.channel === 'PEPPOL') el.icon = 'envelope-solid'; // TOOD: PEPPOL: change icon, e.g. electronic-receipt
									return el;
								}
							});
							contact.channel_endpoints = availableEndpoints;
						}
					}
				})
				.catch(e => {
					Logging.error(e);
				});
		},
		handleTagsFilterChange(value) {
			this.filterTags = value;
		},
		getList(data) {
			const newOffset = data.page * data.limit - data.limit;
			this.offset = newOffset;
			this.getRecipients();
		},
		handleClick(command) {
			switch (command) {
				case 'createRecipient':
					this.$router.push({ name: 'create-recipient' });
					break;
				case 'export':
					Logging.log('handleExport');
					break;
				default:
					break;
			}
		},
		onViewDetail(recp, e) {
			this.$router.push({
				name: 'recipient-detail',
				params: { participant: recp.participant, id: recp.reference }
			});
		},
		translateTag(tag) {
			return TranslationHelper.tag(this, tag);
		},
		async getRecipients() {
			if (this.loading) {
				return;
			}
			this.loading = true;

			try {
				// Data & Total are separate queries: keep them in sync using the (current) query id
				const current_query_id = (Math.random() + 1).toString(36).substring(7);
				this.query_id = current_query_id;

				let legalEntityFilter: Risolto_Recipient_Bool_Exp = {};
				if (this.selectedLegalEntityStore) {
					legalEntityFilter = {
						_or: [
							{ legal_entity: { _eq: this.selectedLegalEntityStore } },
							{ legal_entity: { _is_null: true } }
						]
					};
				}

				let tagsFilter: any = [];
				if (this.filterTags && this.filterTags.length > 0) {
					tagsFilter = [{
						tags: {
							tag_by_reference: {
								tag: { _in: this.filterTags }
							}
						}
					}];
				}

				let searchTerm = this.searchTerm.trim();
				if (searchTerm === '') {
					searchTerm = undefined;
				} else {
					searchTerm = `%${searchTerm}%`;
				}

				let searchTermFilter: Risolto_Recipient_Bool_Exp = {
					_or: [
						{ name: { _ilike: searchTerm } },
						{ account_id: { _ilike: searchTerm } }
					]
				};

				// Add contacts and channel endpoints search if checkbox is checked
				if (this.includeContactsInSearch && searchTermFilter._or) {
					searchTermFilter._or.push(
						{ channel_endpoints:
						{
							_and: [
								{ endpoint: { _ilike: searchTerm } }
							]
						}
						},
						{
							contacts:
							{
								_or: [
									{
										first_name: {
											_ilike: searchTerm
										}
									},
									{
										last_name: {
											_ilike: searchTerm
										}
									},
									{
										full_name: {
											_ilike: searchTerm
										}
									}
								]
							}
						}
					);
				}

				let customFieldsFilter: Risolto_Recipient_Bool_Exp = {
					custom_fields: this.customFieldsFiltersObject
				};

				// Fetch counter for total number of records
				let totalVariables: ViewsRecipient_RecipientsTotalQueryVariables = {
					participant: this.selectedParticipantStore,
					legalEntityFilter: legalEntityFilter,
					tagsFilter: tagsFilter,
					searchTermFilter: searchTermFilter,
					customFieldsFilter: customFieldsFilter
				};

				let variables: ViewsRecipient_RecipientsQueryVariables = {
					participant: this.selectedParticipantStore
				};
				variables.offset = this.offset;
				variables.limit = this.limit;
				variables.orderBy = this.orderBy;
				variables.legalEntityFilter = legalEntityFilter;
				variables.tagsFilter = tagsFilter;
				variables.searchTermFilter = searchTermFilter;
				variables.customFieldsFilter = customFieldsFilter;

				// Launch both queries in parallel but handle them independently
				// Total query runs asynchronously in the background
				this.$apollo.query({
					query: ViewsRecipient_RecipientsTotal,
					variables: totalVariables
				}).then((totalResult) => {
					// Update total count when query completes, with query_id validation
					if (current_query_id === this.query_id && totalResult.data) {
						this.total = totalResult.data.total.aggregate.count;
					}
				}).catch((e) => {
					Logging.error('Error fetching recipient total count:', e);
				});

				// Data query is awaited and handled immediately
				const dataResult = await this.$apollo.query({
					query: ViewsRecipient_Recipients,
					variables: variables
				});

				// Update recipients immediately when data query completes
				if (current_query_id === this.query_id && dataResult.data) {
					this.recipients = dataResult.data.recipients;
					this.recipients.map((recipient) => recipient.reference).forEach((reference) => this.fetchRecipientDataFor(reference));
				}
			} catch (e) {
				Logging.error('Error fetching recipient data:', e);
			}

			this.loading = false;
		},
		fetchRecipientDataFor(reference) {
			this.$apollo
				.query({
					query: RECIPIENT_QUERY,
					variables: { recipient: reference, participant: this.selectedParticipantStore }
				})
				.then(({ data }) => {
					let recipientIndex = this.recipients.findIndex((element) => element.reference === reference);
					if (recipientIndex !== -1) {
						this.recipients[recipientIndex] = {
							...this.recipients[recipientIndex],
							...data.risolto_recipient?.[0]
						};
					}

					this.recipients = [...this.recipients];
				})
				.catch((e) => {

				});
		},
		handleCustomFiltersChange(filters) {
			this.customFieldsFiltersObject = filters;
		},
		toggleRightPanel(value) {
			this.$store.commit('recipients/SET_OVERVIEW_RIGHT_PANEL_COLLAPSED', value);
		},
		evaluateLayout() {
			if (this.recipientsOverviewRightPanelCollapsed) {
				this.rightPanelLayoutVariables.mainPanelWidth = 24;
				this.rightPanelLayoutVariables.rightPanelWidth = 0;
			} else {
				this.rightPanelLayoutVariables.mainPanelWidth = 17;
				this.rightPanelLayoutVariables.rightPanelWidth = 7;
			}
		}
	}
});
